import { useCallback, useEffect, useMemo } from "react";
import { debounce } from "lodash";
import { useFormContext } from "react-hook-form";
import { EMAIL_REGEX } from "@circle-react/components/OnboardingPage/helpers/constants";
import type { EmailVerificationStatus } from "../types";

interface UseEmailValidationProps {
  onEmailChange: (email: string) => void;
  onValidationError: (error: string) => void;
  onValidationStart: () => void;
  onValidationSuccess: (status: EmailVerificationStatus) => void;
  verifyEmail: (email: string) => Promise<{ status: EmailVerificationStatus }>;
}

export const useEmailValidation = ({
  verifyEmail,
  onEmailChange,
  onValidationStart,
  onValidationSuccess,
  onValidationError,
}: UseEmailValidationProps) => {
  const { watch } = useFormContext();
  const formEmailValue = watch("email") || "";

  const debouncedValidateEmail = useMemo(
    () =>
      debounce(async (email: string) => {
        if (!EMAIL_REGEX.test(email)) return;

        onValidationStart();
        try {
          const result = await verifyEmail(email);
          onValidationSuccess(result.status);
        } catch (error) {
          onValidationError("");
        }
      }, 500),
    [verifyEmail, onValidationStart, onValidationSuccess, onValidationError],
  );

  const handleEmailChange = useCallback(
    (email: string) => {
      onEmailChange(email);

      if (EMAIL_REGEX.test(email)) {
        void debouncedValidateEmail(email);
      }
    },
    [onEmailChange, debouncedValidateEmail],
  );

  useEffect(() => {
    const trimmedEmail = formEmailValue.trim();
    if (trimmedEmail) {
      handleEmailChange(trimmedEmail);
    }
  }, [formEmailValue, handleEmailChange]);

  return {
    handleEmailChange,
  };
};
